import React, { useEffect } from 'react';
import SiteRouteHelmet from '../../components/SiteRouteHelmet';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import { getUtmParamsForApi } from '../../utils/utmTracker';
import InlineCheckoutForm from '../../components/InlineCheckoutForm/InlineCheckoutForm';
import { buildSalesLeadCheckoutParams } from '../../utils/salesLeadCheckoutParams';
import useSalesPromptRouting from '../../hooks/useSalesPromptRouting';
import { buildAuthOverlayRouteState } from '../../utils/authOverlayNavigation';
import './SalesPage.css';

/**
 * SalesPage (Control variant)
 *
 * Minimal sales page design - captures lead info and forwards to sales-ai-prompt.
 * NO payment processing on this page - uses InlineCheckoutForm with onContactSuccess
 * to capture lead info only, then redirects to the prompt page.
 */
export default function SalesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showLoginHint,
    loginReturnTo,
    getLeadSuccessPath,
  } = useSalesPromptRouting({ pageVariant: 'control' });

  // Set experiment context for analytics
  useEffect(() => {
    if (!window.__experimentContext?.experiment_name?.includes('sales_page_design')) {
      window.__experimentContext = {
        experiment_name: 'sales_page_design_abc_test',
        experiment_variant: 'control',
        $feature_flag_sales_page_design_variant: 'control',
      };
    }
  }, []);

  // Track page view
  useEffect(() => {
    const utmDataForPageView = getUtmParamsForApi();

    pushEvent({
      event: ANALYTICS_EVENTS.PAGE_VIEW,
      page: 'sales_control',
      source: location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      variant: 'control',
      experiment: 'sales_page_design_abc_test',
      $feature_flag_sales_page_design_variant: 'control',
      ...utmDataForPageView
    });
  }, [location]);

  const handleLoginClick = () => {
    pushEvent({
      event: ANALYTICS_EVENTS.LOGIN_CLICKED,
      method: 'sales_page_link',
      source: location.pathname
    });
    navigate(`/login?returnTo=${encodeURIComponent(loginReturnTo)}`, {
      state: buildAuthOverlayRouteState(location),
    });
  };

  // Handle lead capture success - redirect to sales-choose fork page
  // 🔐 LEAD AUTO-LOGIN: Now includes autoLoginToken for seamless authentication
  const handleContactSuccess = ({ contactData, leadTransactionId, autoLoginToken, howFoundUs, howFoundUsOtherText, existingUser, redirectTo }) => {
    if (existingUser && redirectTo) {
      navigate(redirectTo);
      return;
    }

    // Build query params for choose page (forwarded to whichever path user picks)
    const params = buildSalesLeadCheckoutParams({
      contactData,
      leadTransactionId,
      variant: 'control',
      howFoundUs,
      howFoundUsOtherText,
    });

    navigate(getLeadSuccessPath(params, autoLoginToken));
  };

  return (
    <>
      <SiteRouteHelmet path="/sales" />
      <div className="sales-page-minimal">
        <div className="hero-background">
          <div className="gradient-overlay"></div>
          <div className="floating-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>

      <Container>
        <div className="content-wrapper">
          <div className="hero-content">
            <div className="hero-badge">
              ⚡ LIMITED TIME OFFER
            </div>

            <h1 className="hero-title">
              Build AI Trading Bots
              <span className="gradient-text"> In Minutes</span>
            </h1>

            <p className="hero-subtitle">
              Join 2,300+ traders automating their strategies with AI.
              <strong> No coding required.</strong>
            </p>

            <div className="sales-checkout-container">
              <InlineCheckoutForm
                onContactSuccess={handleContactSuccess}
              />
            </div>

            {showLoginHint && (
              <div className="sales-login-hint">
                Already a member?
                <button type="button" className="sales-login-link" onClick={handleLoginClick}>
                  Log in
                </button>
              </div>
            )}
          </div>
        </div>
      </Container>
    </div>
  </>
  );
}
